import { CommonModule } from '@angular/common';
import { Component, computed, EnvironmentInjector, inject, input, OnInit, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ModalController } from '@ionic/angular/standalone';
import { CheckboxChangeEventDetail, IonCheckboxCustomEvent } from '@ionic/core';
import { fieldSorter, SelectDialogComponent } from '@scandium-oy/ngx-scandium';
import { combineLatest } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { ImagesComponent } from 'src/app/components/images/images.component';
import { SelectDayComponent } from 'src/app/components/select-day/select-day.component';
import { SpeechToTextComponent } from 'src/app/components/speech-to-text/speech-to-text.component';
import { AddUser, ApartmentRoundReport, ReportArea, ReportRoom, RoundType } from 'src/app/models/apartment-round.model';
import { SiteArea } from 'src/app/models/site-area.model';
import { Site } from 'src/app/models/site.model';
import { AreasService } from 'src/app/services/areas.service';
import { apartmentRoundTypes } from 'src/app/utility/report';
import { defaultRoom } from 'src/app/utility/room';
import { AddUserDialogComponent } from './dialog/add-user.dialog';


@Component({
  selector: 'app-apartment-round',
  templateUrl: './apartment-round.component.html',
  styleUrls: ['./apartment-round.component.scss'],
  imports: [
    AppCommonModule,
    CommonModule,
    SelectDayComponent,
    FormsModule,
    SpeechToTextComponent,
    ImagesComponent,
  ],
})
export class ApartmentRoundComponent implements OnInit {
  private areasService = inject(AreasService);
  private injectorContext = inject(EnvironmentInjector);
  private modalCtrl = inject(ModalController);

  private selectedAreas = signal<string[]>([]);

  readonly types = apartmentRoundTypes;

  selectedType = signal<RoundType>(null);
  site = input.required<Site>();
  existing = input<ApartmentRoundReport>(null);
  users = input<AddUser[]>([]);
  report = signal<ApartmentRoundReport>({
    type: '',
    date: new Date(),
    participants: Array.from<AddUser>([]),
    summary: '',
    areas: Array.from<ReportArea>([]),
  });

  siteS = computed(() => this.site());

  areas$ = toObservable(this.siteS).pipe(
    filter((site) => site != null),
    switchMap((site) => combineLatest([this.areasService.getSiteAreas(site.guid), toObservable(this.selectedAreas, { injector: this.injectorContext })])),
    map(([items, areas]) => items.filter((it) => !areas.includes(it.name)).filter((it) => it.name !== defaultRoom).sort(fieldSorter(['name']))),
    shareReplay(1),
  );

  ngOnInit() {
    if (this.existing() != null) {
      this.report.set(this.existing());
      const type = apartmentRoundTypes.find((it) => it.name === this.existing().type);
      this.selectedType.set(type);
    }
  }

  getReport() {
    return this.report();
  }

  selectType(item: RoundType) {
    this.report().type = item.name;
    this.selectedType.set(item);
  }

  onDateChange(value: string) {
    const date = new Date(value);
    this.report().date = date;
  }

  addUser() {
    this.modalCtrl.create({ component: AddUserDialogComponent, cssClass: ['stack-modal'] }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          this.report().participants.push(data.data);
        }
      });
    });
  }

  onRemoveUser(index: number) {
    this.report().participants.splice(index, 1);
  }

  onRemoveArea(index: number) {
    this.report().areas.splice(index, 1);
    this.selectedAreas.update((val) => {
      val.splice(index, 1);
      return val;
    });
  }

  onUser(event: IonCheckboxCustomEvent<CheckboxChangeEventDetail<boolean>>, item: AddUser) {
    const checked = event.detail.checked;
    if (checked) {
      this.report().participants.push(item);
    } else {
      this.report().participants = this.report().participants.filter((it) => it.guid !== item.guid);
    }
  }

  onMic(text: string, field: string, index?: number, type?: string) {
    this.report.update((val) => {
      if (type) {
        const prevText = val.areas[index].type[type][field] as string;
        val.areas[index].type[type][field] = prevText ? `${prevText} ${text}` : text;
        return val;
      } else {
        const prevText = val[field] as string;
        val[field] = prevText ? `${prevText} ${text}` : text;
        return val;
      }
    });
  }

  addArea(items: SiteArea[]) {
    this.modalCtrl.create({ component: SelectDialogComponent, componentProps: { items }, cssClass: ['stack-modal'] }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          const area: ReportArea = { name: data.data.name as string, type: {} };
          this.selectedType().items.map((it) => {
            area.type[it] = {
              blocked: '',
              rooms: [],
            };
          });
          this.report().areas.push(area);
          this.selectedAreas.update((val) => [...val, data.data.name]);
        }
      });
    });
  }

  addRoom(type: string, area: ReportArea) {
    const room = {};
    area.type[type].rooms.push(room);
  }

  onImage(room: ReportRoom, url: string) {
    if (room.images == null) {
      room.images = [url];
    } else {
      room.images.push(url);
    }
  }

  onDeleteImage(room: ReportRoom, url: string) {
    room.images = room.images.filter((it) => it !== url);
  }
}
